import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  apiTopProducts,
  apiSummary,
  apiToGetTransactions,
  apiEmployeeGroup,
  apiEmail,
  apiSingleEmployeeData,
  apiToGetBanksList,
  apiToGetEmployerConfig,
  fetchCategories,
  fetchGroupsAndPayDates,
  fetchGroupPaydates,
  fetchMeApi,
  apiInProgress,
  fetchFileNamesData,
  fetchPICUsersAPI,
  fetchInvoiceApi,
  getAccessTypes,
  getLimitRequestsApplication,
  getLimitRequestsDetails,
  getKYBStatus,
  getKYBHistory,
} from "api/apiCall";

export function fetchTopProducts() {
  return useQuery(
    {
      queryKey: ["topProducts_api"],
      queryFn: apiTopProducts,
    },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchEmail() {
  return useQuery(
    {
      queryKey: ["email_api"],
      queryFn: apiEmail,
      enabled: true,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchEmployeeGroup() {
  return useQuery(
    {
      queryKey: ["employeeGroup_api"],
      queryFn: apiEmployeeGroup,
      enabled: true,
      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchSummary() {
  return useQuery(
    {
      queryKey: ["summary_api"],
      queryFn: apiSummary,
      refetchOnWindowFocus: false,
    },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );
}

export function getTransactionData(payload) {
  return useQuery(
    {
      queryKey: ["get_transaction_data"],
      queryFn: () => apiToGetTransactions(payload),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getSingleEmployeeData(payload) {
  return useQuery(
    {
      queryKey: ["get_single_employee_data"],
      queryFn: () => apiSingleEmployeeData(payload),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getBanksList() {
  return useQuery(
    {
      queryKey: ["get_banks_list"],
      queryFn: () => apiToGetBanksList(),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getCategoriesList(payload) {
  return useQuery(
    {
      queryKey: ["get_categories_list"],
      queryFn: () => fetchCategories({ requestType: payload }),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getGroupsAndPayDatesList() {
  return useQuery(
    {
      queryKey: ["get_groups_and_pay_dates_list"],
      queryFn: () => fetchGroupsAndPayDates(),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function getEmployerConfig() {
  return useQuery(
    {
      queryKey: ["get_employer_config"],
      queryFn: () => apiToGetEmployerConfig(),

      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );
}

export function fetchGroupPaydatesQuery(payload) {
  return useQuery(
    {
      queryKey: ["fetch_group_dates", payload],
      queryFn: () => fetchGroupPaydates(payload),
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  );
}

export function fetchMeApiCall(payload) {
  return useQuery(
    {
      queryKey: ["fetch_me_api", payload],
      queryFn: () => fetchMeApi(payload),
      refetchOnWindowFocus: false,
    },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchProgressTableData(filterData, currentPage, getPageParams, polling) {
  return useQuery(
    {
      queryKey: ["tableData", filterData, currentPage],
      queryFn: () => apiInProgress(getPageParams()),
      refetchInterval: polling ? 2000 : false,
    },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchfileNamesApi(payload) {
  return useQuery(
    {
      queryKey: ["fetchFileNames", payload],
      queryFn: () => fetchFileNamesData(payload),
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    },
  );
}

export function fetchPICUsers() {
  return useQuery(
    { queryKey: ["picUsers"], queryFn: fetchPICUsersAPI, enabled: false },
    { refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false },
  );
}
export function fetchInvoices(filterData, currentPage, getParams) {
  return useQuery(
    {
      queryKey: ["fetchInvoices", filterData, currentPage],
      queryFn: () => fetchInvoiceApi(getParams()),
      refetchOnWindowFocus: false,
    },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchAllAccessTypes() {
  return useQuery(
    { queryKey: ["fetchAccessTypes"], queryFn: () => getAccessTypes() },
    {
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchLimitRequests(payload) {
  const PAGE_LIMIT = 10;
  return useInfiniteQuery(
    ["limit_requests", payload],
    ({ pageParam = 0 }) => getLimitRequestsApplication({ ...payload, skip: pageParam, limit: PAGE_LIMIT }),
    {
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage && lastPage.length === PAGE_LIMIT) {
          return PAGE_LIMIT * allPages.length;
        }
        return false;
      },

      staleTime: 5 * 60 * 1000,
      enabled: true,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );
}

export function fetchLimitRequestDetails(payload) {
  return useQuery(
    { queryKey: ["fetchLimitRequestDetails"], queryFn: () => getLimitRequestsDetails(payload), enabled: false },
    {
      enabled: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchKYBStatus() {
  return useQuery(
    { queryKey: ["fetchKYBStatus"], queryFn: () => getKYBStatus() },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}

export function fetchKYBHistory() {
  return useQuery(
    { queryKey: ["kybHistory"], queryFn: () => getKYBHistory(), enabled: false },
    {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: true,
    },
  );
}
